import { useState } from "react";
import { toast } from "react-toastify";
import { post } from "../../utils/api";
import ConfirmationModal from "../../customComponents/ConfirmationModal";

/**
 * Drives the post-save "N packages use this — update their prices now?" prompt.
 *
 * Usage in a master-data form (hotel/transport/addon/settings):
 *
 *   const { promptReprice, RepriceModal } = useRepricePrompt();
 *   ...
 *   const response = await post("/addHotel", payload);
 *   toast.success("Saved");
 *   promptReprice(response, () => navigate("/hotels"));  // navigate runs after the user decides
 *   ...
 *   return (<>{RepriceModal}{/* rest of JSX *\/}</>);
 *
 * If the save did not affect any package (affectedPackageCount === 0) the prompt
 * is skipped and onDone runs immediately. "Update all" calls the synchronous
 * /repriceAffectedPackages endpoint; it is safe to retry because recomputePackage
 * is idempotent.
 */
export default function useRepricePrompt() {
  const [state, setState] = useState({
    open: false,
    count: 0,
    trigger: null,
    onDone: null,
    busy: false,
  });

  const finish = () => {
    const onDone = state.onDone;
    setState({ open: false, count: 0, trigger: null, onDone: null, busy: false });
    onDone?.();
  };

  const promptReprice = (response, onDone) => {
    const count = Number(response?.affectedPackageCount) || 0;
    const trigger = response?.repriceTrigger || null;
    if (!count || !trigger) {
      onDone?.();
      return;
    }
    setState({ open: true, count, trigger, onDone: onDone || null, busy: false });
  };

  const handleConfirm = async () => {
    setState((s) => ({ ...s, busy: true }));
    try {
      const res = await post("/repriceAffectedPackages", state.trigger);
      const updated = res?.data?.updated?.length ?? 0;
      const failed = res?.data?.failed?.length ?? 0;
      if (failed > 0) {
        toast.warn(
          `${updated} package(s) repriced, ${failed} failed. Save the item again to retry.`
        );
      } else {
        toast.success(`${updated} package(s) repriced.`);
      }
    } catch (e) {
      toast.error("Repricing failed. Save the item again to retry.");
    } finally {
      finish();
    }
  };

  const RepriceModal = (
    <ConfirmationModal
      isOpen={state.open}
      onClose={finish}
      onConfirm={handleConfirm}
      title="Update package prices?"
      message={`${state.count} package${
        state.count === 1 ? "" : "s"
      } use this item. Update their prices now?`}
      confirmLabel={state.busy ? "Updating…" : "Update all"}
      confirmClassName="bg-blue-600 text-white px-4 py-2 rounded"
    />
  );

  return { promptReprice, RepriceModal };
}
